import React from 'react';
import { Shield, TrendingUp, Heart, Store, Building2, Users } from 'lucide-react';

const Priorities = () => {
  const priorities = [
    {
      icon: Shield,
      title: 'Public Safety',
      description: 'Safe neighborhoods are the foundation of a thriving community. I am committed to working with law enforcement and community leaders to ensure strong emergency response, well-lit streets, neighborhood watch programs, and proactive safety measures that protect our families and businesses.',
      commitment: 'Ensuring every resident feels safe in their home and neighborhood',
      color: 'text-purple-700',
      bgColor: 'bg-purple-50',
      gradient: 'from-purple-50 to-purple-100',
    },
    {
      icon: TrendingUp,
      title: 'Economic Growth',
      description: 'Kissimmee deserves a robust economy that creates quality jobs and opportunities for all. I will champion initiatives that attract new businesses, support workforce development, invest in infrastructure, and create partnerships that bring sustainable economic prosperity to our city.',
      commitment: 'Creating pathways to financial stability and career success',
      color: 'text-amber-600',
      bgColor: 'bg-amber-50',
      gradient: 'from-amber-50 to-amber-100',
    },
    {
      icon: Heart,
      title: 'Support for Families',
      description: 'Strong families build strong communities. I will advocate for programs that support working parents, expand access to quality childcare, invest in youth programs and recreational facilities, and ensure our families have the resources they need to thrive and grow together.',
      commitment: 'Investing in programs that strengthen and empower families',
      color: 'text-rose-600',
      bgColor: 'bg-rose-50',
      gradient: 'from-rose-50 to-rose-100',
    },
    {
      icon: Store,
      title: 'Small Business Development',
      description: 'Small businesses are the heartbeat of Kissimmee. As a business owner for over 20 years, I understand the challenges entrepreneurs face. I will fight to reduce regulatory barriers, provide access to resources and funding, create networking opportunities, and foster an environment where local businesses can flourish.',
      commitment: 'Empowering entrepreneurs to achieve their dreams',
      color: 'text-emerald-600',
      bgColor: 'bg-emerald-50',
      gradient: 'from-emerald-50 to-emerald-100',
    },
    {
      icon: Building2,
      title: 'Responsible City Growth',
      description: 'Growth is essential, but it must be managed wisely. I will ensure development projects respect our community character, preserve green spaces, improve traffic flow, invest in quality infrastructure, and maintain the charm that makes Kissimmee special while preparing for our future.',
      commitment: 'Balancing progress with preservation of our community identity',
      color: 'text-blue-600',
      bgColor: 'bg-blue-50',
      gradient: 'from-blue-50 to-blue-100',
    },
    {
      icon: Users,
      title: 'Community Representation',
      description: 'Every voice matters. I will be accessible, transparent, and accountable to all residents. Through regular town halls, community forums, open office hours, and active engagement, I will ensure that your concerns are heard, your ideas are valued, and your needs are represented in every decision.',
      commitment: 'Being a voice for all residents, not just a few',
      color: 'text-purple-600',
      bgColor: 'bg-purple-50',
      gradient: 'from-purple-50 to-purple-100',
    },
  ];

  return (
    <section id="priorities" className="relative py-20 md:py-32 overflow-hidden">
      {/* Dynamic Purple Background */}
      <div className="absolute inset-0 bg-gradient-to-br from-purple-50 via-purple-100/50 to-white"></div>
      <div className="absolute inset-0 opacity-40" style={{
        backgroundImage: `radial-gradient(circle at 80% 20%, rgba(124, 58, 237, 0.15) 0%, transparent 50%),
                         radial-gradient(circle at 20% 80%, rgba(167, 139, 250, 0.12) 0%, transparent 50%),
                         radial-gradient(circle at 50% 50%, rgba(196, 181, 253, 0.08) 0%, transparent 70%)`
      }}></div>
      
      <div className="relative container mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center max-w-4xl mx-auto mb-20">
          <h2 className="text-4xl md:text-5xl lg:text-6xl font-bold text-gray-900 mb-6">
            Campaign Priorities
          </h2>
          <div className="w-24 h-1.5 bg-purple-700 mx-auto mb-6"></div>
          <p className="text-xl md:text-2xl text-gray-600 leading-relaxed">
            My commitment to building a stronger, more vibrant Kissimmee through focused action on the issues that matter most to our community
          </p>
        </div>

        <div className="space-y-12 max-w-6xl mx-auto">
          {priorities.map((priority, index) => {
            const Icon = priority.icon;
            const isEven = index % 2 === 0;
            
            return (
              <div
                key={index}
                className={`relative overflow-hidden rounded-3xl shadow-2xl hover:shadow-purple-200 transition-all duration-500 bg-white/80 backdrop-blur-sm border border-purple-100/50`}
              >
                {/* Gradient Accent Bar */}
                <div className={`absolute top-0 left-0 right-0 h-2 bg-gradient-to-r ${priority.gradient}`}></div>
                <div className="absolute top-0 right-0 w-64 h-64 opacity-10">
                  <Icon className={`w-full h-full ${priority.color}`} strokeWidth={0.5} />
                </div>
                
                <div className="relative p-8 md:p-12">
                  <div className="flex flex-col md:flex-row md:items-start gap-6">
                    {/* Icon */}
                    <div className={`${priority.bgColor} w-20 h-20 rounded-2xl flex items-center justify-center flex-shrink-0 shadow-lg border-2 border-white`}>
                      <Icon className={`${priority.color} w-10 h-10`} strokeWidth={2.5} />
                    </div>
                    
                    {/* Content */}
                    <div className="flex-1 space-y-4">
                      <div>
                        <div className="flex items-center gap-3 mb-3">
                          <h3 className="text-3xl md:text-4xl font-bold text-gray-900">
                            {priority.title}
                          </h3>
                          <div className={`hidden md:block flex-1 h-1 ${priority.bgColor} rounded-full`}></div>
                        </div>
                        <p className="text-lg text-gray-700 leading-relaxed">
                          {priority.description}
                        </p>
                      </div>
                      
                      {/* Commitment Badge */}
                      <div className="inline-flex items-center gap-2 bg-white rounded-full px-6 py-3 shadow-md">
                        <div className={`w-2 h-2 rounded-full ${priority.color.replace('text-', 'bg-')}`}></div>
                        <span className="text-sm font-semibold text-gray-800 italic">
                          {priority.commitment}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default Priorities;
